import { Vector3 } from "three";
import { Ioc } from "@core/ioc/ioc";
import type { Vec3 } from "@models/data/entityData";
import type { WorkflowRevertCamera } from "@models/workflow/WorkflowRevertState";
import { ORBIT_CAMERA, type OrbitCameraController } from "./orbitCameraController";

/**
 * Camera adapter for workflow script revert — the web stand-in for Unity's
 * `ISceneModel.CameraPosition` / `CameraLookAt` pair that
 * `PreviewRestoreState` captures and restores.
 *
 * Lives in `runtime/` because `src/models/**` must not import `src/runtime/**`
 * (or Three.js): the revert bag declares the seam it needs and the composition
 * root hands it this. `null` whenever no viewport is mounted, which is also
 * how a script running on the projects grid ends up not touching the camera.
 */
export function resolveRevertCamera(): WorkflowRevertCamera | null {
  if (!Ioc.isRegistered<OrbitCameraController>(ORBIT_CAMERA)) return null;
  const camera = Ioc.resolve<OrbitCameraController>(ORBIT_CAMERA);

  return {
    getPose: () => {
      const pose = camera.getPose();
      return { position: toVec3(pose.position), target: toVec3(pose.target) };
    },
    setPose: (position: Vec3, target: Vec3) => {
      camera.setPose(new Vector3().fromArray(position), new Vector3().fromArray(target));
    },
  };
}

function toVec3(value: Vector3): Vec3 {
  return [value.x, value.y, value.z];
}
